import {
  NarratorText,
  PatternSpotlightCard,
  TierBody,
  TierButton,
  TierCard,
  TierHeadline,
  TransformationInsightCard,
  cn,
  dramaBadgeStyles,
  formatDramaScore,
  getDramaBadgeContent,
  getDramaCategoryColor,
  getDramaScoreColor
} from "./chunk-RVO37TTB.mjs";

// src/components/Gauges/DramaScoreGauge.tsx
import React, { useEffect } from "react";
import { View } from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedProps,
  withSpring,
  useReducedMotion
} from "react-native-reanimated";
import Svg, { Circle, Path } from "react-native-svg";
import { useTranslation } from "@dramadetector/shared";
var AnimatedCircle = Animated.createAnimatedComponent(Circle);
var AnimatedPath = Animated.createAnimatedComponent(Path);
function getDramaColor(score) {
  if (score <= 2) return "#22c55e";
  if (score <= 5) return "#f59e0b";
  if (score <= 7) return "#f97316";
  return "#ef4444";
}
function getDramaCategory(score) {
  if (score <= 2) return "safe";
  if (score <= 5) return "caution";
  if (score <= 7) return "high_drama";
  return "extreme_drama";
}
function getTierStrokeWidth(tierLevel) {
  const tierStrokeMap = {
    free: 2,
    drama_plus: 3,
    dramax: 4
  };
  return tierStrokeMap[tierLevel];
}
function getGaugeAngle(score) {
  const normalized = Math.max(0, Math.min(10, score)) / 10;
  return normalized * 270 - 90;
}
function getNeedlePath(size, angle, strokeWidth) {
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = (size - strokeWidth) / 2 - 8;
  const rad = angle * Math.PI / 180;
  const endX = centerX + radius * Math.cos(rad);
  const endY = centerY + radius * Math.sin(rad);
  return `M ${centerX} ${centerY} L ${endX} ${endY}`;
}
function DramaScoreGauge({
  score,
  size = 200,
  tierLevel = "free",
  animated = true,
  testID = "drama-score-gauge"
}) {
  const { t } = useTranslation("analysis");
  const reducedMotion = useReducedMotion();
  const needleAngle = useSharedValue(getGaugeAngle(0));
  const tierStrokeWidth = getTierStrokeWidth(tierLevel);
  const radius = (size - tierStrokeWidth) / 2;
  useEffect(() => {
    const targetAngle = getGaugeAngle(score);
    if (!animated || reducedMotion) {
      needleAngle.value = targetAngle;
    } else {
      needleAngle.value = withSpring(targetAngle, {
        damping: 15,
        stiffness: 100,
        mass: 1
      });
    }
  }, [score, needleAngle, animated, reducedMotion]);
  const animatedNeedleProps = useAnimatedProps(() => {
    const path = getNeedlePath(size, needleAngle.value, tierStrokeWidth);
    return {
      d: path
    };
  });
  const color = getDramaColor(score);
  const category = getDramaCategory(score);
  const accessibilityLabel = t("score_card.accessibility.drama_score_value", {
    score: score.toFixed(1),
    category
  });
  return /* @__PURE__ */ React.createElement(
    View,
    {
      className: "items-center justify-center rounded-2xl bg-white/70 p-6",
      accessibilityLabel,
      accessibilityRole: "progressbar",
      testID
    },
    /* @__PURE__ */ React.createElement(Svg, { width: size, height: size, viewBox: `0 0 ${size} ${size}` }, /* @__PURE__ */ React.createElement(
      Circle,
      {
        cx: size / 2,
        cy: size / 2,
        r: radius,
        stroke: "#e5e7eb",
        strokeWidth: tierStrokeWidth,
        fill: "none",
        strokeDasharray: `${2 * Math.PI * radius * 240 / 360} ${2 * Math.PI * radius * 120 / 360}`,
        strokeDashoffset: 0,
        strokeLinecap: "round",
        transform: `rotate(-90 ${size / 2} ${size / 2})`
      }
    ), /* @__PURE__ */ React.createElement(
      Circle,
      {
        cx: size / 2,
        cy: size / 2,
        r: radius,
        stroke: color,
        strokeWidth: tierStrokeWidth,
        fill: "none",
        strokeDasharray: `${2 * Math.PI * radius * 240 / 360} ${2 * Math.PI * radius * 120 / 360}`,
        strokeDashoffset: `${2 * Math.PI * radius * 240 / 360 * (1 - score / 10)}`,
        strokeLinecap: "round",
        transform: `rotate(-90 ${size / 2} ${size / 2})`
      }
    ), /* @__PURE__ */ React.createElement(
      AnimatedPath,
      {
        animatedProps: animatedNeedleProps,
        stroke: color,
        strokeWidth: 2,
        strokeLinecap: "round"
      }
    ), /* @__PURE__ */ React.createElement(
      Circle,
      {
        cx: size / 2,
        cy: size / 2,
        r: 4,
        fill: color
      }
    )),
    /* @__PURE__ */ React.createElement(View, { className: "absolute items-center" }, /* @__PURE__ */ React.createElement(
      TierHeadline,
      {
        size: "3xl",
        weight: "bold",
        align: "center",
        testID: `${testID}-score`
      },
      score.toFixed(1)
    ), /* @__PURE__ */ React.createElement(
      TierBody,
      {
        size: "sm",
        color: "text-gray-600",
        className: "mt-1",
        align: "center",
        testID: `${testID}-label`
      },
      t("score_card.drama_score")
    ))
  );
}

// src/components/Tables/FeatureComparisonTable/index.tsx
import React5, { useEffect as useEffect2 } from "react";
import { View as View5, ScrollView } from "react-native";
import { useTranslation as useTranslation4 } from "@dramadetector/shared";

// src/components/Tables/FeatureComparisonTable/TableHeader.tsx
import React2 from "react";
import { View as View2 } from "react-native";
import { useTranslation as useTranslation2 } from "@dramadetector/shared";
function getHeaderStyle(tier, isHighlighted) {
  const baseClasses = "p-4 rounded-tier items-center justify-center min-h-[140px]";
  if (isHighlighted) {
    return `${baseClasses} bg-gradient-to-br from-amber-50/60 to-white/40 border-2 border-amber-300/60 backdrop-blur-sm`;
  }
  const styleMap = {
    free: "bg-white/30 border border-gray-200/40 backdrop-blur-sm",
    premium: "bg-white/40 border border-amber-200/40 backdrop-blur-md",
    drama: "bg-white/40 border border-amber-200/40 backdrop-blur-md",
    dramax: "bg-white/40 border border-slate-300/40 backdrop-blur-md",
    enterprise: "bg-white/40 border border-slate-300/40 backdrop-blur-md"
  };
  return `${baseClasses} ${styleMap[tier.id] || "bg-white/30 border border-gray-200/40 backdrop-blur-sm"}`;
}
function TableHeader({
  tiers,
  highlightTier,
  className = "",
  testID
}) {
  const { t } = useTranslation2(["subscription", "common"]);
  return /* @__PURE__ */ React2.createElement(
    View2,
    {
      className: `flex-row gap-2 ${className}`,
      testID,
      accessible: true,
      accessibilityRole: "header"
    },
    tiers.map((tier) => {
      const isHighlighted = highlightTier === tier.id;
      const headerStyle = getHeaderStyle(tier, isHighlighted);
      return /* @__PURE__ */ React2.createElement(
        View2,
        {
          key: tier.id,
          className: `flex-1 ${headerStyle}`,
          testID: `header-${tier.id}`
        },
        /* @__PURE__ */ React2.createElement(View2, { className: "w-full items-center" }, tier.popular && /* @__PURE__ */ React2.createElement(
          View2,
          {
            className: "mb-3 px-3 py-1.5 rounded-full bg-amber-400/80 backdrop-blur-sm",
            testID: `popular-badge-${tier.id}`
          },
          /* @__PURE__ */ React2.createElement(TierBody, { size: "xs", weight: "semibold", color: "text-white", className: "tracking-wide", testID: `popular-text-${tier.id}` }, t("subscription.comparison.most_popular", {
            defaultValue: "MOST POPULAR"
          }))
        ), /* @__PURE__ */ React2.createElement(
          TierHeadline,
          {
            size: "lg",
            weight: "bold",
            align: "center",
            color: tier.id === "free" ? "text-gray-900" : tier.id === "dramax" || tier.id === "enterprise" ? "text-slate-900" : "text-amber-900",
            testID: `tier-name-${tier.id}`
          },
          tier.name
        ), /* @__PURE__ */ React2.createElement(View2, { className: "mt-2 items-center", testID: `price-section-${tier.id}` }, /* @__PURE__ */ React2.createElement(View2, { className: "flex-row items-baseline gap-0.5" }, /* @__PURE__ */ React2.createElement(TierBody, { size: "lg", weight: "normal", color: "text-gray-500", testID: `price-prefix-${tier.id}` }, tier.price === "0" ? t("common.free", { defaultValue: "Free" }) : "$"), tier.price !== "0" && /* @__PURE__ */ React2.createElement(TierHeadline, { size: "3xl", weight: "bold", testID: `price-amount-${tier.id}` }, tier.price), tier.price !== "0" && /* @__PURE__ */ React2.createElement(TierBody, { size: "sm", color: "text-gray-600", testID: `price-suffix-${tier.id}` }, "/mo"))), tier.price !== "0" && /* @__PURE__ */ React2.createElement(
          TierBody,
          {
            size: "xs",
            color: "text-gray-500",
            align: "center",
            className: "mt-1.5",
            testID: `billing-note-${tier.id}`
          },
          t("subscription.comparison.billed_monthly", {
            defaultValue: "Billed monthly"
          })
        ))
      );
    })
  );
}

// src/components/Tables/FeatureComparisonTable/TableRow.tsx
import React4 from "react";
import { View as View4 } from "react-native";

// src/components/Tables/FeatureComparisonTable/FeatureCell.tsx
import React3 from "react";
import { View as View3 } from "react-native";
function getTierColor(tierId) {
  const colorMap = {
    free: "text-green-600",
    premium: "text-amber-500",
    // Gold
    drama: "text-amber-500",
    // Gold (Drama+)
    dramax: "text-slate-300",
    // Platinum
    enterprise: "text-slate-300"
    // Platinum
  };
  return colorMap[tierId] || "text-gray-600";
}
function getBackgroundStyle(tierId, isPopular) {
  const baseClasses = "bg-white/40 backdrop-blur-md rounded-lg p-3 border";
  if (isPopular) {
    return `${baseClasses} border-amber-400/50 bg-amber-50/20`;
  }
  const borderMap = {
    free: "border-gray-200/30",
    premium: "border-amber-200/30",
    drama: "border-amber-200/30",
    dramax: "border-slate-300/30",
    enterprise: "border-slate-300/30"
  };
  return `${baseClasses} ${borderMap[tierId] || "border-gray-200/30"}`;
}
function FeatureCell({
  value,
  tierId,
  isPopular = false,
  className = "",
  testID
}) {
  const backgroundColor = getBackgroundStyle(tierId, isPopular);
  const tierColor = getTierColor(tierId);
  if (value === true) {
    return /* @__PURE__ */ React3.createElement(
      View3,
      {
        className: `${backgroundColor} items-center justify-center min-h-[60px] ${className}`,
        testID,
        accessible: true,
        accessibilityRole: "text",
        accessibilityLabel: "Feature included"
      },
      /* @__PURE__ */ React3.createElement(TierBody, { size: "lg", weight: "semibold", color: tierColor, testID: `${testID}-checkmark` }, "\u2713")
    );
  }
  if (value === false) {
    return /* @__PURE__ */ React3.createElement(
      View3,
      {
        className: `${backgroundColor} items-center justify-center min-h-[60px] ${className}`,
        testID,
        accessible: true,
        accessibilityRole: "text",
        accessibilityLabel: "Feature not included"
      },
      /* @__PURE__ */ React3.createElement(TierBody, { size: "lg", weight: "normal", color: "text-gray-300", testID: `${testID}-cross` }, "\u2717")
    );
  }
  return /* @__PURE__ */ React3.createElement(
    View3,
    {
      className: `${backgroundColor} items-center justify-center min-h-[60px] ${className}`,
      testID,
      accessible: true,
      accessibilityRole: "text",
      accessibilityLabel: String(value)
    },
    /* @__PURE__ */ React3.createElement(
      TierBody,
      {
        size: "sm",
        weight: "semibold",
        align: "center",
        color: tierColor,
        className: "px-2"
      },
      String(value)
    )
  );
}

// src/components/Tables/FeatureComparisonTable/TableRow.tsx
import { useTranslation as useTranslation3 } from "@dramadetector/shared";
function TableRow({
  feature,
  tiers,
  isAlternate = false,
  highlightTier,
  className = "",
  testID
}) {
  const { t } = useTranslation3(["subscription", "common"]);
  const backgroundClass = isAlternate ? "bg-white/15 backdrop-blur-xs" : "bg-transparent";
  return /* @__PURE__ */ React4.createElement(
    View4,
    {
      className: `flex-row gap-2 p-3 rounded-lg ${backgroundClass} ${className}`,
      testID,
      accessible: true,
      accessibilityRole: "text",
      accessibilityLabel: `Feature: ${feature.name}`
    },
    /* @__PURE__ */ React4.createElement(
      View4,
      {
        className: "flex-1 min-w-[140px] justify-center pr-2",
        testID: `feature-name-${feature.name.replace(/\s+/g, "-").toLowerCase()}`
      },
      /* @__PURE__ */ React4.createElement(
        TierBody,
        {
          size: "sm",
          weight: "medium",
          color: "text-gray-800",
          className: "leading-snug"
        },
        feature.name
      )
    ),
    /* @__PURE__ */ React4.createElement(View4, { className: "flex-row gap-2 flex-3" }, tiers.map((tier) => {
      const value = feature.tiers[tier.id];
      const isPopular = tier.popular;
      const isHighlighted = highlightTier === tier.id;
      return /* @__PURE__ */ React4.createElement(View4, { key: tier.id, className: "flex-1" }, /* @__PURE__ */ React4.createElement(
        FeatureCell,
        {
          value,
          tierId: tier.id,
          isPopular,
          testID: `feature-cell-${feature.name.replace(/\s+/g, "-").toLowerCase()}-${tier.id}`
        }
      ));
    }))
  );
}

// src/components/Tables/FeatureComparisonTable/index.tsx
function FeatureComparisonTable({
  tiers,
  features,
  highlightTier,
  onSelectTier,
  onView,
  className = "",
  testID
}) {
  const { t } = useTranslation4("subscription");
  useEffect2(() => {
    if (onView) {
      onView({
        highlighted_tier: highlightTier,
        total_tiers: tiers.length,
        total_features: features.length
      });
    }
  }, [highlightTier, tiers.length, features.length, onView]);
  return /* @__PURE__ */ React5.createElement(
    View5,
    {
      className: `w-full bg-gradient-to-b from-white/5 to-white/1 rounded-2xl p-4 ${className}`,
      testID,
      accessible: true,
      accessibilityRole: "list",
      accessibilityLabel: t("subscription.comparison.title", {
        defaultValue: "Feature comparison"
      })
    },
    /* @__PURE__ */ React5.createElement(
      ScrollView,
      {
        horizontal: true,
        showsHorizontalScrollIndicator: false,
        scrollEventThrottle: 16,
        className: "w-full",
        testID: `${testID}-scroll`
      },
      /* @__PURE__ */ React5.createElement(View5, { className: "min-w-full px-2" }, /* @__PURE__ */ React5.createElement(
        TableHeader,
        {
          tiers,
          highlightTier,
          testID: `${testID}-header`
        }
      ), /* @__PURE__ */ React5.createElement(View5, { className: "mt-4 gap-1", testID: `${testID}-features` }, features.map((feature, index) => /* @__PURE__ */ React5.createElement(
        TableRow,
        {
          key: `${feature.name}-${index}`,
          feature,
          tiers,
          isAlternate: index % 2 === 1,
          highlightTier,
          testID: `${testID}-row-${index}`
        }
      ))))
    ),
    /* @__PURE__ */ React5.createElement(
      View5,
      {
        className: "mt-3 items-center",
        testID: `${testID}-scroll-hint`,
        accessible: true,
        accessibilityRole: "text"
      },
      /* @__PURE__ */ React5.createElement(TierBody, { size: "xs", color: "text-gray-400" }, t("subscription.comparison.scroll_hint", {
        defaultValue: "Swipe to see more \u2192"
      }))
    )
  );
}

// src/components/Lists/TimelineGroup.tsx
import React6 from "react";
import { View as View6, Pressable, Platform } from "react-native";
import { useTranslation as useTranslation5 } from "@dramadetector/shared";
var getCategoryColors = (category) => {
  const colors = {
    safe: {
      bg: "bg-drama-safe/10",
      text: "text-drama-safe",
      badge: "bg-drama-safe/20 text-drama-safe"
    },
    caution: {
      bg: "bg-drama-caution/10",
      text: "text-drama-caution",
      badge: "bg-drama-caution/20 text-drama-caution"
    },
    high_drama: {
      bg: "bg-drama-high/10",
      text: "text-drama-high",
      badge: "bg-drama-high/20 text-drama-high"
    },
    extreme_drama: {
      bg: "bg-drama-extreme/10",
      text: "text-drama-extreme",
      badge: "bg-drama-extreme/20 text-drama-extreme"
    },
    // Legacy fallback for old data still labeled 'risky'
    risky: {
      bg: "bg-drama-extreme/10",
      text: "text-drama-extreme",
      badge: "bg-drama-extreme/20 text-drama-extreme"
    }
  };
  return colors[category] || {
    bg: "bg-gray-50",
    text: "text-gray-700",
    badge: "bg-gray-100 text-gray-700"
  };
};
var TimelineItem = React6.memo(
  ({
    item,
    onPress,
    onSwipeEdit,
    onSwipeDelete
  }) => {
    const { t } = useTranslation5("features");
    const colors = getCategoryColors(item.category);
    const messagePreview = item.message.length > 80 ? `${item.message.substring(0, 80)}...` : item.message;
    const formatTime = (dateString) => {
      const date = new Date(dateString);
      const now = /* @__PURE__ */ new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 6e4);
      const diffHours = Math.floor(diffMs / 36e5);
      if (diffMins < 1) return t("history_item.time.just_now");
      if (diffMins < 60)
        return t("history_item.time.minutes_ago", { count: diffMins });
      if (diffHours < 24)
        return t("history_item.time.hours_ago", { count: diffHours });
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit"
      });
    };
    return /* @__PURE__ */ React6.createElement(
      Pressable,
      {
        onPress: () => onPress(item),
        onLongPress: () => {
        },
        className: "bg-white/40 backdrop-blur-md rounded-lg p-3 mb-2 border border-white/20 active:opacity-75",
        style: {
          cursor: "pointer",
          ...Platform.OS === "web" && {
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)"
          }
        },
        accessibilityRole: "button",
        accessibilityLabel: t("history_item.accessibility.analysis_result", {
          category: item.category,
          score: item.score.toFixed(1)
        })
      },
      /* @__PURE__ */ React6.createElement(View6, { className: "flex-row justify-between items-center mb-2" }, /* @__PURE__ */ React6.createElement(
        View6,
        {
          className: `${colors.badge} px-2 py-1 rounded-full flex-row items-center`
        },
        /* @__PURE__ */ React6.createElement(TierBody, { size: "xs", weight: "semibold", testID: `score-${item.id}` }, item.score.toFixed(1)),
        /* @__PURE__ */ React6.createElement(TierBody, { size: "xs", weight: "medium", className: "ml-1 uppercase", testID: `category-${item.id}` }, t(`history_item.category.${item.category}`))
      ), /* @__PURE__ */ React6.createElement(TierBody, { size: "xs", color: "text-gray-900", testID: `time-${item.id}` }, formatTime(item.createdAt))),
      /* @__PURE__ */ React6.createElement(
        TierBody,
        {
          size: "sm",
          color: "text-gray-800",
          className: "leading-relaxed",
          testID: `message-${item.id}`
        },
        messagePreview
      )
    );
  }
);
TimelineItem.displayName = "TimelineItem";
function TimelineGroup({
  date,
  items,
  onItemPress,
  onItemSwipeEdit,
  onItemSwipeDelete,
  testID
}) {
  return /* @__PURE__ */ React6.createElement(View6, { testID, className: "mb-4" }, /* @__PURE__ */ React6.createElement(
    View6,
    {
      className: "flex-row items-center justify-between px-4 py-3 rounded-lg bg-white/30 backdrop-blur-md border border-white/20",
      style: Platform.OS === "web" ? {
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)"
      } : void 0,
      accessibilityLabel: `${date}, ${items.length} items`
    },
    /* @__PURE__ */ React6.createElement(View6, { className: "flex-row items-center flex-1" }, /* @__PURE__ */ React6.createElement(
      TierHeadline,
      {
        size: "lg",
        weight: "semibold",
        className: "text-gray-900",
        testID: `group-date-${testID}`
      },
      date
    ), /* @__PURE__ */ React6.createElement(
      View6,
      {
        className: "bg-white/50 backdrop-blur-sm rounded-full px-2.5 py-1 ml-3 border border-white/30",
        style: Platform.OS === "web" ? {
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)"
        } : void 0
      },
      /* @__PURE__ */ React6.createElement(
        TierBody,
        {
          size: "xs",
          weight: "semibold",
          color: "text-gray-800",
          testID: `group-count-${testID}`
        },
        items.length
      )
    ))
  ), /* @__PURE__ */ React6.createElement(View6, { className: "px-4 py-2" }, items.map((item) => /* @__PURE__ */ React6.createElement(
    TimelineItem,
    {
      key: item.id,
      item,
      onPress: onItemPress,
      onSwipeEdit: onItemSwipeEdit,
      onSwipeDelete: onItemSwipeDelete
    }
  ))));
}

// src/components/PatternSpotlightCardSkeleton.tsx
import React7 from "react";
import { View as View7 } from "react-native";
function PatternSpotlightCardSkeleton({
  className = "",
  testID = "pattern-spotlight-card-skeleton"
}) {
  return /* @__PURE__ */ React7.createElement(TierCard, { variant: "premium", className, testID }, /* @__PURE__ */ React7.createElement(View7, { className: "grid grid-cols-12 gap-4 animate-pulse" }, /* @__PURE__ */ React7.createElement(View7, { className: "col-span-8" }, /* @__PURE__ */ React7.createElement(View7, { className: "h-7 bg-gray-200 rounded w-48" })), /* @__PURE__ */ React7.createElement(View7, { className: "col-span-4 flex items-end justify-start" }, /* @__PURE__ */ React7.createElement(View7, { className: "h-9 w-20 bg-accent-blush border border-accent-gold rounded-tier" })), /* @__PURE__ */ React7.createElement(View7, { className: "col-span-12 space-y-2" }, /* @__PURE__ */ React7.createElement(View7, { className: "h-5 bg-gray-200 rounded w-full" }), /* @__PURE__ */ React7.createElement(View7, { className: "h-5 bg-gray-200 rounded w-5/6" })), /* @__PURE__ */ React7.createElement(View7, { className: "col-span-6" }, /* @__PURE__ */ React7.createElement(View7, { className: "h-8 w-32 bg-accent-blush border-l-2 border-accent-gold rounded-tier" }))));
}

// src/components/TransformationInsightCardSkeleton.tsx
import React8 from "react";
import { View as View8 } from "react-native";
function TransformationInsightCardSkeleton({
  className = "",
  testID = "transformation-insight-card-skeleton"
}) {
  return /* @__PURE__ */ React8.createElement(TierCard, { variant: "premium", className: `bg-black ${className}`, testID }, /* @__PURE__ */ React8.createElement(View8, { className: "grid grid-cols-12 gap-4 animate-pulse" }, /* @__PURE__ */ React8.createElement(View8, { className: "col-span-8" }, /* @__PURE__ */ React8.createElement(View8, { className: "h-7 bg-gray-700 rounded w-56" })), /* @__PURE__ */ React8.createElement(View8, { className: "col-span-4 flex items-end justify-start" }, /* @__PURE__ */ React8.createElement(View8, { className: "h-9 w-20 bg-transparent border border-accent-gold rounded-tier" })), /* @__PURE__ */ React8.createElement(View8, { className: "col-span-12 md:col-span-6" }, /* @__PURE__ */ React8.createElement(View8, { className: "h-5 bg-accent-gold/30 rounded w-20 mb-2" }), /* @__PURE__ */ React8.createElement(View8, { className: "p-4 bg-gray-900 border-l-2 border-red-500 rounded-tier space-y-2" }, /* @__PURE__ */ React8.createElement(View8, { className: "h-4 bg-gray-700 rounded w-full" }), /* @__PURE__ */ React8.createElement(View8, { className: "h-4 bg-gray-700 rounded w-5/6" }), /* @__PURE__ */ React8.createElement(View8, { className: "h-4 bg-gray-700 rounded w-4/6" }))), /* @__PURE__ */ React8.createElement(View8, { className: "col-span-12 md:col-span-6" }, /* @__PURE__ */ React8.createElement(View8, { className: "h-5 bg-accent-gold/30 rounded w-16 mb-2" }), /* @__PURE__ */ React8.createElement(View8, { className: "p-4 bg-gray-900 border-l-2 border-green-500 rounded-tier space-y-2" }, /* @__PURE__ */ React8.createElement(View8, { className: "h-4 bg-gray-700 rounded w-full" }), /* @__PURE__ */ React8.createElement(View8, { className: "h-4 bg-gray-700 rounded w-5/6" }), /* @__PURE__ */ React8.createElement(View8, { className: "h-4 bg-gray-700 rounded w-4/6" }))), /* @__PURE__ */ React8.createElement(View8, { className: "col-span-12" }, /* @__PURE__ */ React8.createElement(View8, { className: "h-5 bg-accent-gold/30 rounded w-24 mb-2" }), /* @__PURE__ */ React8.createElement(View8, { className: "space-y-2" }, /* @__PURE__ */ React8.createElement(View8, { className: "flex flex-row" }, /* @__PURE__ */ React8.createElement(View8, { className: "h-4 w-4 bg-accent-gold/30 rounded mr-2" }), /* @__PURE__ */ React8.createElement(View8, { className: "h-4 bg-gray-700 rounded flex-1" })), /* @__PURE__ */ React8.createElement(View8, { className: "flex flex-row" }, /* @__PURE__ */ React8.createElement(View8, { className: "h-4 w-4 bg-accent-gold/30 rounded mr-2" }), /* @__PURE__ */ React8.createElement(View8, { className: "h-4 bg-gray-700 rounded flex-1" })), /* @__PURE__ */ React8.createElement(View8, { className: "flex flex-row" }, /* @__PURE__ */ React8.createElement(View8, { className: "h-4 w-4 bg-accent-gold/30 rounded mr-2" }), /* @__PURE__ */ React8.createElement(View8, { className: "h-4 bg-gray-700 rounded flex-1" })))), /* @__PURE__ */ React8.createElement(View8, { className: "col-span-6" }, /* @__PURE__ */ React8.createElement(View8, { className: "h-8 w-32 bg-gray-700 border-l-2 border-gray-600 rounded-tier" }))));
}

// src/components/button.tsx
import { cva } from "class-variance-authority";
var buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md font-medium transition-all disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground",
        destructive: "bg-destructive text-white",
        outline: "border border-input bg-background",
        secondary: "bg-secondary text-secondary-foreground",
        ghost: "hover:bg-accent",
        link: "text-primary underline-offset-4"
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);

// src/components/card.tsx
var cardStyles = {
  card: "bg-card flex flex-col gap-6 rounded-xl border border-border p-6 shadow-sm",
  header: "flex flex-col gap-2",
  title: "text-xl font-semibold leading-none tracking-tight",
  description: "text-sm text-muted-foreground",
  content: "",
  footer: "flex flex-row items-center"
};

// src/components/badge.tsx
import { cva as cva2 } from "class-variance-authority";
var badgeVariants = cva2(
  "inline-flex items-center justify-center rounded-full border px-2.5 py-0.5",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        destructive: "border-transparent bg-destructive text-white",
        outline: "border-input text-foreground"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);

// src/components/progress.tsx
var progressStyles = {
  container: "relative h-2 w-full overflow-hidden rounded-full bg-primary/20",
  indicator: "h-full bg-primary transition-all"
};
function calculateProgressPercentage(value, max = 100) {
  return Math.min(Math.max(value / max * 100, 0), 100);
}

// src/components/drama-score.tsx
var dramaScoreSizes = {
  sm: "text-2xl",
  md: "text-4xl",
  lg: "text-6xl"
};
var dramaScoreStyles = {
  container: "flex flex-col items-center gap-1",
  label: "text-xs text-muted-foreground uppercase tracking-wide"
};
function getDramaScoreStyles(score, size = "md") {
  const colorClass = score === null ? "text-blue-600" : getDramaScoreColor(score);
  return {
    scoreClass: `font-bold ${colorClass} ${dramaScoreSizes[size]}`,
    formattedScore: formatDramaScore(score)
  };
}

// src/components/drama-card.tsx
var dramaCardStyles = {
  container: "bg-card flex flex-col gap-6 rounded-xl border border-border p-6 shadow-sm",
  header: "flex flex-row items-center justify-between",
  headerLeft: "flex-1",
  title: "text-xl font-semibold",
  description: "text-sm text-muted-foreground",
  content: "flex flex-col gap-4",
  divider: "border-t border-border pt-4",
  summary: "text-sm text-muted-foreground",
  issuesTitle: "text-sm font-semibold mb-2",
  issueRow: "flex flex-row gap-2 mb-1",
  issueBullet: "text-sm text-muted-foreground",
  issueText: "text-sm text-muted-foreground flex-1"
};
function formatIssueCount(count) {
  return `${count} issue${count !== 1 ? "s" : ""} detected`;
}

// src/lib/theme.ts
import { semantic, drama, primitive } from "@dramadetector/shared/design/tokens";
var defaultTheme = {
  textPrimary: semantic.text.primary,
  textSecondary: semantic.text.secondary,
  textTertiary: semantic.text.tertiary,
  backgroundPrimary: semantic.bg.surface,
  backgroundSecondary: semantic.bg.elevated,
  backgroundTertiary: semantic.bg.muted,
  borderPrimary: semantic.border.subtle,
  borderSecondary: semantic.border.default,
  switchTrackActive: primitive.color.accentViolet,
  switchTrackInactive: semantic.border.subtle,
  switchThumbActive: primitive.color.accentVioletDark,
  switchThumbInactive: semantic.bg.elevated,
  statusSuccess: semantic.status.success,
  statusWarning: semantic.status.warning,
  statusError: semantic.status.error,
  infoBackground: semantic.info.bg,
  infoText: semantic.info.text,
  infoBorder: semantic.info.border,
  brandPrimary: primitive.color.brand[500]
};
var getTheme = () => defaultTheme;
var getCategoryColors2 = (category) => {
  const colors = {
    safe: {
      bg: "bg-drama-safe/10",
      text: "text-drama-safe",
      badge: "bg-drama-safe/20 text-drama-safe"
    },
    caution: {
      bg: "bg-drama-caution/10",
      text: "text-drama-caution",
      badge: "bg-drama-caution/20 text-drama-caution"
    },
    high_drama: {
      bg: "bg-drama-high/10",
      text: "text-drama-high",
      badge: "bg-drama-high/20 text-drama-high"
    },
    extreme_drama: {
      bg: "bg-drama-extreme/10",
      text: "text-drama-extreme",
      badge: "bg-drama-extreme/20 text-drama-extreme"
    },
    risky: {
      bg: "bg-drama-extreme/10",
      text: "text-drama-extreme",
      badge: "bg-drama-extreme/20 text-drama-extreme"
    }
  };
  return colors[category] || {
    bg: "bg-gray-50",
    text: "text-gray-700",
    badge: "bg-gray-100 text-gray-700"
  };
};
var getDramaColor2 = (score) => {
  if (score <= drama.thresholds.safeMax) {
    return drama.category.safe;
  } else if (score <= drama.thresholds.cautionMax) {
    return drama.category.caution;
  } else if (score <= drama.thresholds.highDramaMax) {
    return drama.category.highDrama;
  } else {
    return drama.category.extremeDrama;
  }
};
var themeClasses = {
  textPrimary: "text-gray-900",
  textSecondary: "text-gray-800",
  textTertiary: "text-gray-600",
  bgSecondary: "bg-gray-50",
  bgTertiary: "bg-gray-100",
  borderPrimary: "border-gray-200",
  borderSecondary: "border-gray-300",
  infoBg: "bg-blue-50",
  infoText: "text-blue-700",
  infoBorder: "border-blue-200"
};
export {
  DramaScoreGauge,
  FeatureCell,
  FeatureComparisonTable,
  NarratorText,
  PatternSpotlightCard,
  PatternSpotlightCardSkeleton,
  TableHeader,
  TableRow,
  TierBody,
  TierButton,
  TierCard,
  TierHeadline,
  TimelineGroup,
  TransformationInsightCard,
  TransformationInsightCardSkeleton,
  badgeVariants,
  buttonVariants,
  calculateProgressPercentage,
  cardStyles,
  cn,
  defaultTheme,
  dramaBadgeStyles,
  dramaCardStyles,
  dramaScoreSizes,
  dramaScoreStyles,
  formatDramaScore,
  formatIssueCount,
  getCategoryColors2 as getCategoryColors,
  getDramaBadgeContent,
  getDramaCategoryColor,
  getDramaColor2 as getDramaColor,
  getDramaScoreColor,
  getDramaScoreStyles,
  getTheme,
  progressStyles,
  themeClasses
};
//# sourceMappingURL=index.mjs.map